import React from "react";
import { useNavigate } from "react-router-dom";
import { Play, ArrowRight } from "lucide-react";
import { resolveFileUrl } from "../api";

/**
 * Full-bleed featured video hero.
 * Props:
 *   video           — the featured video object (must have id, title, artist, cover_image)
 *   onBrowse?       — handler for the secondary CTA (defaults to navigate('/videos'))
 *   browseLabel?    — secondary CTA label (defaults 'Browse Videos')
 */
export default function FeaturedVideoHero({ video, onBrowse, browseLabel = "Browse Videos" }) {
  const navigate = useNavigate();
  if (!video) return null;

  const handleBrowse = onBrowse || (() => navigate("/videos"));
  const handleWatch = () => navigate(`/videos/${video.id}`);

  return (
    <section
      className="relative grain-bg border-b border-neutral-800 overflow-hidden"
      data-testid="featured-video-hero"
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(5,5,5,0.35) 0%, rgba(5,5,5,0.85) 92%), url('${resolveFileUrl(video.cover_image)}')`,
          backgroundSize: "cover",
          backgroundPosition: "center 35%",
        }}
      />
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 py-16 sm:py-20 min-h-[68vh] flex flex-col justify-end">
        <span
          className="self-start bg-[var(--accent)] text-black font-mono-label text-[11px] px-3 py-1 mb-6 rise"
          data-testid="featured-chip"
        >
          FEATURED
        </span>
        <h1
          className="font-display text-5xl sm:text-7xl md:text-8xl uppercase leading-[0.92] max-w-[1100px] rise rise-2"
          data-testid="featured-video-title"
        >
          {video.title}
        </h1>
        <div
          className="font-headline text-lg sm:text-2xl uppercase text-neutral-200 mt-4 rise rise-3"
          data-testid="featured-video-artist"
        >
          {video.artist}
        </div>
        <div className="flex flex-wrap gap-3 mt-8 rise rise-4">
          <button onClick={handleWatch} className="btn-primary text-sm" data-testid="featured-watch-now">
            <Play size={16} /> Watch Now
          </button>
          <button onClick={handleBrowse} className="btn-ghost text-sm" data-testid="featured-browse-videos">
            {browseLabel} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
